import { Check, ArrowRight, Phone, Search, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEO } from '../components/SEO';

const packs = [
  {
    name: 'Pack Fondation',
    price: '749',
    tagline: "L'essentiel pour exister en ligne",
    seoLevel: 'SEO complet',
    features: [
      'Site monopage',
      'Version PC et mobile',
      'Design sur-mesure aux couleurs de l\'entreprise',
      'Sections : présentation, services, zone d\'intervention, contact',
      'Animations soignées',
      'Suivi post-livraison',
    ],
    highlighted: false,
  },
  {
    name: 'Pack Structure',
    price: '999',
    tagline: 'Un site complet pour convaincre vos prospects',
    seoLevel: 'SEO avancé',
    features: [
      'Site 4 à 5 pages',
      'Version PC et mobile',
      'Design sur-mesure aux couleurs de l\'entreprise',
      'Sections : présentation, services, zone d\'intervention, contact',
      'Galerie de réalisations photos',
      'Formulaire de contact fonctionnel',
      'Animations soignées',
      'Suivi post-livraison',
    ],
    highlighted: true,
    badge: 'Le plus demandé',
  },
  {
    name: 'Pack Gros Oeuvre',
    price: '1 699',
    tagline: 'La vitrine premium qui génère des leads',
    seoLevel: 'SEO optimal',
    features: [
      'Site large, jusqu\'à 10 pages',
      'Version PC et mobile',
      'Design sur-mesure aux couleurs de l\'entreprise',
      'Sections : présentation, services, zone d\'intervention, contact',
      'Galerie de réalisations photos',
      'Formulaire de contact fonctionnel',
      'Page avant/après réalisations détaillées',
      'Formulaire de demande de devis qualifié',
      'Animations soignées',
      'Suivi post-livraison',
    ],
    highlighted: false,
  },
];

export default function LaColabPage() {
  return (
    <main data-testid="lacolab-page" className="min-h-screen bg-background">
      <SEO
        title="Partenariat Axeris Labs x LaColab – Offres exclusives"
        description="Offres exclusives de création de sites web pour les clients LaColab. Tarifs préférentiels sur des sites vitrines professionnels sur-mesure."
        path="/lacolab"
        noIndex={true}
      />

      {/* Header */}
      <section className="pt-32 pb-16">
        <div className="container-main">
          <div className="text-center animate-fade-up">
            {/* Logos as title */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <a href="https://axeris-labs.fr" target="_blank" rel="noopener noreferrer">
                <img
                  src="/images/logos/logo-color.png"
                  alt="Axeris Labs"
                  className="h-10 md:h-12 w-auto object-contain hover:opacity-80 transition-opacity"
                />
              </a>
              <span className="text-2xl font-extralight text-muted-foreground/50">x</span>
              <a href="https://lacolab.biz/" target="_blank" rel="noopener noreferrer">
                <img
                  src="/images/logos/lacolab-logo.png"
                  alt="LaColab"
                  className="h-10 md:h-12 w-auto object-contain hover:opacity-80 transition-opacity"
                />
              </a>
            </div>

            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              En tant que client LaColab, bénéficiez de tarifs préférentiels sur la création de votre site web professionnel.
            </p>
          </div>
        </div>
      </section>

      {/* Packs */}
      <section className="pb-20" data-testid="lacolab-packs">
        <div className="container-main">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {packs.map((pack, index) => (
              <Card
                key={pack.name}
                data-testid={`lacolab-pack-${index}`}
                className={`relative overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  pack.highlighted
                    ? 'border-2 border-orange-500 shadow-lg shadow-orange-500/10'
                    : 'border-2 border-transparent hover:border-orange-500/20'
                }`}
              >
                {pack.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-red-500" />
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl">{pack.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{pack.tagline}</p>
                  {pack.highlighted && (
                    <Badge className="absolute top-4 right-4 bg-orange-500 text-white hover:bg-orange-600">
                      {pack.badge}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <div className="mb-6">
                    <span className="text-4xl font-black">{pack.price}</span>
                    <span className="text-muted-foreground ml-1">€</span>
                  </div>

                  {pack.seoLevel && (
                    <div className="mb-4 p-3 rounded-lg bg-orange-500/5 border border-orange-500/15">
                      <p className="text-sm font-semibold text-orange-600 dark:text-orange-400 flex items-center gap-2">
                        <Search className="h-4 w-4" />
                        {pack.seoLevel}
                      </p>
                    </div>
                  )}

                  <ul className="space-y-3 mb-8 flex-1">
                    {pack.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <a href="#contact-lacolab" className="mt-auto">
                    <Button
                      data-testid={`lacolab-cta-${index}`}
                      className={`w-full ${
                        pack.highlighted
                          ? 'bg-orange-500 hover:bg-orange-600 text-white'
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                    >
                      Choisir ce pack
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Processus */}
      <section className="section-padding" data-testid="lacolab-process">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Comment ça se passe ?</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Simple, rapide, sans prise de tête.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { step: '1', title: 'On échange', desc: 'Un appel de 30 min à 1h pour comprendre votre activité, vos besoins et vos objectifs.' },
              { step: '2', title: 'Je crée', desc: 'Je conçois votre site sur-mesure. Première version livrée sous 7 jours.' },
              { step: '3', title: 'On ajuste', desc: 'Quelques allers-retours si nécessaire, jusqu\'à ce que le résultat vous convienne.' },
              { step: '4', title: 'En ligne', desc: 'Votre site est mis en ligne, prêt à recevoir vos premiers clients.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-orange-500 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hébergement */}
      <section className="bg-muted/30 section-padding" data-testid="lacolab-hosting">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Hébergement & maintenance</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Après la création, votre site a besoin d'un toit. Maintenance et support inclus, vous n'avez rien à gérer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Card className="border-2 border-orange-500 shadow-lg shadow-orange-500/10">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Hébergement</CardTitle>
                <p className="text-sm text-muted-foreground">L'essentiel pour rester en ligne</p>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <span className="text-3xl font-black">15</span>
                  <span className="text-muted-foreground ml-1">€/mois</span>
                </div>
                <ul className="space-y-2">
                  {['Hébergement sécurisé', 'Certificat SSL inclus', 'Sauvegardes régulières', 'Maintenance serveur continue', 'Support technique réactif'].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-orange-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-orange-500/20 transition-colors">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Hébergement + Emails</CardTitle>
                <p className="text-sm text-muted-foreground">Si votre site envoie des emails automatiques</p>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <span className="text-3xl font-black">17,50</span>
                  <span className="text-muted-foreground ml-1">€/mois</span>
                </div>
                <ul className="space-y-2">
                  {['Tout le plan Hébergement', 'Notifications automatiques', 'Confirmations de commande', 'Récupération de mot de passe', 'Alertes et rappels'].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-orange-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Contact */}
      <section id="contact-lacolab" className="bg-muted/30 section-padding" data-testid="lacolab-contact">
        <div className="container-main text-center">
          <div className="max-w-2xl mx-auto animate-fade-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Intéressé par un pack ?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contactez-moi directement pour discuter de votre projet.
              Mentionnez le partenariat LaColab pour bénéficier de ces tarifs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="https://axeris-labs.fr/tarifs#contact">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6" data-testid="lacolab-email-cta">
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="https://axeris-labs.fr/tarifs#contact">
                <Button variant="outline" className="text-lg px-8 py-6 border-orange-500/30 hover:bg-orange-500/5" data-testid="lacolab-phone-cta">
                  <Phone className="mr-2 h-5 w-5" />
                  Me contacter
                </Button>
              </a>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Prix nets, non assujetti à la TVA. Paiement en 3x sans frais disponible.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
